/**
 * Games Info Animation
 */
define(function(require, exports, module) {
    'use strict';

    var $ = require('j');
    var Class = require('Class');
    var GamesInfoView = require('portlets/games-info/gamesinfo.view');
    var config = require('config');
    var device = require('util/device.util');

    var GamesInfoAnimation = Class.create({

        _isComingUpEnabled: false,
        _isRearrangeEnabled: false,

        ELEMENTS_APPEAR_DURATION: 600,
        ANIMATION_EL_CLASS_NAME: 'gamesinfo__item-animation-wrap',
        get _animationElSelector() {
            return '.' + this.ANIMATION_EL_CLASS_NAME;
        },

        initialize: function(gamesInfoView) {
            if (!gamesInfoView) {
                throw new Error('Cannot initialize GamesInfoAnimation module without GamesInfoView.');
            }
            this._gamesInfoView = gamesInfoView;
        },

        /**
         * @public
         */
        prepareAnimations: function() {
            this._savePreviousAnimableGames();
        },

        /**
         * @public
         */
        runAnimations: function() {
            this._animateGames();
        },

        /**
         * Collect data about previous positions of games
         * @private
         */
        _savePreviousAnimableGames: function() {
            if (this._gamesInfoView.model.categories.getActive().get('isLoading')) {
                return;
            }

            if (!this._gamesInfoView.$games || !this._gamesInfoView.$games.length) {
                this._gamesPositions = null;
                return;
            }

            var $gameWraps = this._gamesInfoView.$games
                .find('.fn-game-items')
                .eq(this._gamesInfoView._getActivePage())
                .find(this._animationElSelector);
            if (!$gameWraps.length || !$gameWraps[0].offsetParent) {
                this._gamesPositions = null;
                return;
            }

            // Feature: smooth interrupt. Take current coordinates so if we interrupt current animation - objects will
            // start to move from their current visible positions.
            // May use `el.offsetLeft` & `el.offsetTop` to increase performance (and remove this feature).
            var parentRect = $gameWraps[0].offsetParent.getBoundingClientRect();
            this._gamesPositions = $gameWraps.reduce(function(memo, el) {
                var gameCode = this._getGameCode(el);
                if (gameCode) {
                    var clientRect = el.getBoundingClientRect();
                    memo[gameCode] = {
                        left: clientRect.left - parentRect.left,
                        top: clientRect.top - parentRect.top
                    };
                }
                return memo;
            }.bind(this), {});
        },

        /**
         * Gets game code from rendered game element
         * @param {HTMLElement} el
         * @returns {String}
         * @private
         */
        _getGameCode: function(el) {
            return $(el).data('gameCode') || $('[data-game-code]', el).data('gameCode');
        },

        _getAnimableGameItems: function() {
            this._gamesInfoView.$games
                .find('.gamesinfo__item')
                .forEach(function(el) {
                    el.classList.add(this.ANIMATION_EL_CLASS_NAME);
                }.bind(this));

            return this._gamesInfoView.$games
                .find('.fn-game-items')
                .eq(0)
                .find(this._animationElSelector);
        },

        /**
         * "Fade-in" [default] animation
         * @private
         */
        _setFadeInAnimation: function($list, el) {
            $list.push(el);
            this._setPrefixedStyle(el, 'transform', 'scale(0.2)');
            el.style.opacity = '0';
        },

        /**
         * "Coming-Up" animation
         * @private
         */
        _setComingUpAnimation: function($list, elAppearHeight, el) {
            $list.push(el);
            this._setPrefixedStyle(el, 'transform', 'translateY(' + elAppearHeight + 'px)');
            el.style.opacity = '0';
        },

        /**
         * "Rearrange" animation
         * @private
         */
        _setRearrangeAnimation: function($list, previousPosition, el) {
            $list.push(el);
            var left = previousPosition.left - el.offsetLeft;
            var top = previousPosition.top - el.offsetTop;
            if (left || top) {
                this._setPrefixedStyle(el, 'transform', 'translate(' + left + 'px, ' + top + 'px)');
            }
        },

        _startAnimation: function(el) {
            this._setPrefixedStyle(el, 'transform', '');
            el.style.opacity = '';
        },

        _startAnimationDelayed: function($list) {
            var singleElAppearDuration = Math.floor(this.ELEMENTS_APPEAR_DURATION / ($list.length || 1));
            $list.forEach(function(el, index) {
                setTimeout(this._startAnimation.bind(this, el), index * singleElAppearDuration);
            }.bind(this));
        },

        /**
         * Run animations on games
         * @private
         */
        _animateGames: function() {
            var $animableGameItems = this._getAnimableGameItems();
            var $initialGameItems = $();
            var $movedGameItems = $();
            var $newGameItems = $();
            // var $removedGameItems = $(); // todo later
            var sortField = this._gamesInfoView.model.get('sortField');
            var isRearrange = this._gamesPositions && this._sortField !== sortField;

            /*
             First, we determine what type of animation SHOULD be shown. Then we check if it is available (via org
             settings), and if not - using fallback to default animation. So, effectively, we cannot disable default
             animation unless we disable all animations at all.
             */
            if (this._isFirstTimeShow) {
                if (this._isComingUpEnabled) {
                    var elAppearHeight = Math.floor(window.innerHeight / 2);
                    $animableGameItems.forEach(this._setComingUpAnimation.bind(this, $initialGameItems, elAppearHeight));
                } else {
                    $animableGameItems.forEach(this._setFadeInAnimation.bind(this, $newGameItems));
                }
                this._isFirstTimeShow = false;
            } else if (isRearrange) {
                if (this._isRearrangeEnabled) {
                    $animableGameItems.forEach(function(el) {
                        var gameCode = this._getGameCode(el);
                        var previousPosition = this._gamesPositions[gameCode];

                        if (previousPosition) {
                            this._setRearrangeAnimation($movedGameItems, previousPosition, el);
                        } else {
                            this._setFadeInAnimation($newGameItems, el);
                        }
                    }.bind(this));
                } else {
                    $animableGameItems.forEach(this._setFadeInAnimation.bind(this, $newGameItems));
                }
            } else {
                $animableGameItems.forEach(this._setFadeInAnimation.bind(this, $newGameItems));
            }

            // BUG: browser sometimes ignores setting and removing style or css class,
            // because it is removed too quickly.
            // `setTimeout` is a dirty hack to solve this problem.
            // case:
            // 1. set 'transform'
            // -> elements moved to "initial" state immediately
            // 2. add 'transition'
            // 3. remove `transform`
            // -> elements should move to "target" state with animation
            // But the problem here is that animation sometime is not triggered because element was not initially moved
            // with `transform`. `setTimeout` solves this by making a gap in execution stack for browser rendering
            // engine to have time to apply changes.
            // Maybe, `requestAnimationFrame` method could solve this problem in more elegant and correct way.
            window.setTimeout(function() {
                $animableGameItems.forEach(function(el) {
                    el.classList.add('animate-games');
                });

                $movedGameItems.forEach(this._startAnimation.bind(this));
                this._startAnimationDelayed($initialGameItems);
                this._startAnimationDelayed($newGameItems);

                this._fixBugs($animableGameItems);
            }.bind(this), 0);

            this._sortField = sortField;
        },

        _setPrefixedStyle: function(el, prop, value) {
            var prefixedProp = '-webkit-' + prop;

            el.style[prefixedProp] = value;
            el.style[prop] = value;
        },

        _fixBugs: function($animableGameItems) {
            var transitionDuration = this.ELEMENTS_APPEAR_DURATION +
                (parseFloat($animableGameItems.css('transitionDuration')) * 1000 || this.ELEMENTS_APPEAR_DURATION / 2);

            this._redrawGamesInfoView($animableGameItems, transitionDuration);
            this._fixLastRowItemsDisplay(transitionDuration);
        },

        _redrawGamesInfoView: function($animableGameItems, transitionDuration) {
            var isIe11 = device.browserName === 'ie' && device.browserVersion === '11';
            var isEdge = device.browserName === 'edge';

            if (!isIe11 && !isEdge) {
                return;
            }

            window.clearTimeout(this._ie11RedrawTimeout);
            this._ie11RedrawTimeout = window.setTimeout(function() {
                var preservedGamesInfoHeight = this._gamesInfoView.$games.css('height');
                this._gamesInfoView.$games.css('height', preservedGamesInfoHeight);
                $animableGameItems.forEach(function(gameEl) {
                    this._gamesInfoView.redraw(gameEl);
                }.bind(this));
                this._gamesInfoView.$games.css('height', '');
            }.bind(this), transitionDuration);
        },

        _fixLastRowItemsDisplay: function(transitionDuration) {
            window.setTimeout(function() {
                this._gamesInfoView.fixLastRowItemsDisplay();
            }.bind(this), transitionDuration);
        }

    });

    GamesInfoAnimation.patchGamesInfoView = function() {
        GamesInfoAnimation._patchGamesInfoGameItems();
        GamesInfoAnimation._patchGamesInfoSearch();
    };

    GamesInfoAnimation._patchGamesInfoGameItems = function() {
        var originalInitialize = GamesInfoView.prototype.initialize;
        var originalRender = GamesInfoView.prototype.render;
        var originalRenderGames = GamesInfoView.prototype.renderGames;

        GamesInfoView.prototype.initialize = function() {
            var result = originalInitialize.apply(this, arguments);
            this.animation = new GamesInfoAnimation(this);
            return result;
        };

        GamesInfoView.prototype.render = function() {
            this.animation._isFirstTimeShow = true;
            return originalRender.apply(this, arguments);
        };

        GamesInfoView.prototype.renderGames = function() {
            this.animation.prepareAnimations();
            var result = originalRenderGames.apply(this, arguments);
            this.animation.runAnimations();
            return result;
        };

        GamesInfoAnimation.prototype._isComingUpEnabled = config.get('animationConfig.gamesinfoComingUp');
        GamesInfoAnimation.prototype._isRearrangeEnabled = config.get('animationConfig.gamesinfoRearrange');
    };

    GamesInfoAnimation._patchGamesInfoSearch = function() {
        if (!config.get('animationConfig.gamesinfoSearch')) {
            return;
        }

        var originalRenderControls = GamesInfoView.prototype.renderControls;
        GamesInfoView.prototype.renderControls = function() {
            var result = originalRenderControls.apply(this, arguments);
            this
                .$('.gamesinfo__search-wrapper')
                .addClass('gamesinfo__search-wrapper--animate');
            return result;
        };
    };

    module.exports = GamesInfoAnimation;

});
